import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClockIcon,
  DocumentCheckIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import DashboardShell from './components/DashboardShell';

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

const STAGES = {
  setup_needed: { label: 'Set up workflow', tone: 'red' },
  creditor_agreement_needed: { label: 'Waiting on creditor agreement', tone: 'amber' },
  packet_incomplete: { label: 'Packet incomplete', tone: 'amber' },
  ready_to_send: { label: 'Ready to send', tone: 'blue' },
  waiting_for_client: { label: 'Waiting on client', tone: 'amber' },
  client_correction_needed: { label: 'Client correction needed', tone: 'red' },
  review_client_upload: { label: 'Review client upload', tone: 'blue' },
  final_copy_needed: { label: 'Final signed copy needed', tone: 'amber' },
  creditor_filing_followup: { label: 'Follow up on filing', tone: 'red' },
  filed_copy_missing: { label: 'Filed — final copy missing', tone: 'amber' },
  complete: { label: 'Complete and filed', tone: 'green' },
};

const BADGE_TONES = {
  red: 'bg-red-50 text-red-700 ring-red-200',
  amber: 'bg-amber-50 text-amber-800 ring-amber-200',
  blue: 'bg-blue-50 text-blue-700 ring-blue-200',
  green: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  slate: 'bg-slate-100 text-slate-700 ring-slate-200',
};

function buildUrl(path) {
  return `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
}

async function apiFetch(path) {
  const token = localStorage.getItem('accessToken');
  const response = await fetch(buildUrl(path), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.detail || data.error || `Request failed (${response.status})`);
  }
  return data;
}

function formatDate(value, includeTime = false) {
  if (!value) return 'Not set';
  const date = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(`${value}T12:00:00`)
    : new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...(includeTime ? { hour: 'numeric', minute: '2-digit' } : {}),
  });
}

function formatMoney(value) {
  if (value === null || value === undefined || value === '') return 'Balance not entered';
  const amount = Number(value);
  if (!Number.isFinite(amount)) return value;
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function deadlineLabel(caseRow) {
  if (!caseRow.reaffirmation_deadline) return 'Deadline not set';
  if (caseRow.days_remaining < 0) {
    const days = Math.abs(caseRow.days_remaining);
    return `${days} day${days === 1 ? '' : 's'} overdue`;
  }
  if (caseRow.days_remaining === 0) return 'Due today';
  return `${caseRow.days_remaining} day${caseRow.days_remaining === 1 ? '' : 's'} left`;
}

function deadlineTone(caseRow) {
  if (caseRow.deadline_state === 'overdue' || caseRow.deadline_state === 'urgent') return 'red';
  if (caseRow.deadline_state === 'soon' || caseRow.deadline_state === 'missing') return 'amber';
  return 'slate';
}

function Badge({ children, tone = 'slate' }) {
  return (
    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${BADGE_TONES[tone]}`}>
      {children}
    </span>
  );
}

function SummaryCard({ label, value, description, tone, active, onClick }) {
  const surface = {
    red: 'border-red-200 bg-red-50/50',
    amber: 'border-amber-200 bg-amber-50/50',
    blue: 'border-blue-200 bg-blue-50/50',
    green: 'border-emerald-200 bg-emerald-50/50',
    slate: 'border-slate-200 bg-white',
  }[tone || 'slate'];
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-xl border p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow ${surface} ${active ? 'ring-2 ring-indigo-500' : ''}`}
    >
      <p className="text-2xl font-bold text-slate-900">{value || 0}</p>
      <p className="mt-1 text-sm font-semibold text-slate-800">{label}</p>
      <p className="mt-1 text-xs text-slate-500">{description}</p>
    </button>
  );
}

function ProgressStep({ complete, label, warning }) {
  return (
    <div className={`flex items-center gap-1.5 text-xs ${complete ? 'font-semibold text-emerald-700' : warning ? 'font-semibold text-amber-700' : 'text-slate-400'}`}>
      <span className={`flex h-4 w-4 items-center justify-center rounded-full border ${complete ? 'border-emerald-600 bg-emerald-600 text-white' : warning ? 'border-amber-500 bg-amber-50' : 'border-slate-300 bg-white'}`}>
        {complete ? '✓' : ''}
      </span>
      {label}
    </div>
  );
}

function ReaffirmationRow({ item, caseRow }) {
  const stage = STAGES[item.stage] || { label: item.stage, tone: 'slate' };
  const clientComplete = item.attachments.client_signed
    || item.attachments.final_signed
    || ['accepted'].includes(item.workflow_status);

  return (
    <div className="border-t border-slate-200 px-4 py-4 first:border-t-0 sm:px-5">
      <div className="grid gap-4 xl:grid-cols-[minmax(220px,1.2fr)_minmax(330px,1.6fr)_minmax(250px,1.25fr)]">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold text-slate-900">{item.creditor_name}</h3>
            <Badge tone={stage.tone}>{stage.label}</Badge>
          </div>
          <p className="mt-1 text-sm text-slate-600">
            {formatMoney(item.balance)} · {item.who_owes_display || item.who_owes}
          </p>
          <p className="mt-1 text-sm text-slate-600">
            <span className="font-medium text-slate-700">Collateral:</span>{' '}
            {item.collateral?.length ? item.collateral.join('; ') : 'Not linked'}
          </p>
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Progress</p>
          <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            <ProgressStep complete label="Reaffirmation needed" />
            <ProgressStep complete={item.attachments.agreement} label="Agreement received" />
            <ProgressStep complete={clientComplete} label="Client completed" />
            <ProgressStep complete={item.attachments.final_signed} label="Final copy saved" />
            <ProgressStep complete={item.filed} warning={Boolean(caseRow.possible_unmatched_dockets?.length)} label="Filed with court" />
          </div>
          {item.sent_at && (
            <p className="mt-2 text-xs text-slate-500">Last sent to client {formatDate(item.sent_at, true)}</p>
          )}
        </div>

        <div className="rounded-lg bg-slate-50 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Next action</p>
          <p className="mt-1 text-sm font-medium text-slate-800">{item.next_action}</p>
          {item.filed_docket && (
            <p className="mt-2 text-xs text-emerald-700">
              Docket #{item.filed_docket.docket_number}
              {item.filed_docket.filed_at ? ` · ${formatDate(item.filed_docket.filed_at)}` : ''}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function CaseCard({ caseRow, initiallyOpen }) {
  const [open, setOpen] = useState(initiallyOpen);
  const attentionCount = caseRow.reaffirmations.filter((item) => item.needs_attention).length;
  const completeCount = caseRow.reaffirmations.length - attentionCount;

  return (
    <article className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-4 sm:px-5">
        <button type="button" onClick={() => setOpen(!open)} className="flex min-w-0 items-center gap-3 text-left">
          {open ? <ChevronDownIcon className="h-5 w-5 shrink-0 text-slate-500" /> : <ChevronRightIcon className="h-5 w-5 shrink-0 text-slate-500" />}
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="truncate text-base font-bold text-slate-900">{caseRow.debtor_names || 'Debtor'}</h2>
              <span className="text-sm font-medium text-slate-500">{caseRow.case_number || caseRow.case_id}</span>
              <Badge tone={deadlineTone(caseRow)}>{deadlineLabel(caseRow)}</Badge>
            </div>
            <p className="mt-1 text-sm text-slate-600">
              Filed {formatDate(caseRow.date_filed)} · Reaffirmation deadline {formatDate(caseRow.reaffirmation_deadline)} · {attentionCount} needing attention · {completeCount} complete
            </p>
          </div>
        </button>
        <div className="flex flex-wrap gap-2">
          <a href={`/case/${caseRow.case_id}/reaffirmations`} className="rounded-md bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-500">Open reaffirmations</a>
          <a href={`/case/${caseRow.case_id}/dockets`} className="rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50">View dockets</a>
          <a href={`/case/${caseRow.case_id}`} className="rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50">Case home</a>
        </div>
      </div>

      {open && (
        <>
          {caseRow.possible_unmatched_dockets?.length > 0 && (
            <div className="border-t border-amber-200 bg-amber-50 px-4 py-3 sm:px-5">
              <div className="flex gap-2 text-sm text-amber-900">
                <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" />
                <div>
                  <p className="font-semibold">Possible reaffirmation docket needs review</p>
                  {caseRow.possible_unmatched_dockets.map((docket) => (
                    <p key={docket.id} className="mt-1 text-xs">Docket #{docket.docket_number}: {docket.docket_text}</p>
                  ))}
                </div>
              </div>
            </div>
          )}
          <div>
            {caseRow.reaffirmations.map((item) => (
              <ReaffirmationRow key={item.creditor_id} item={item} caseRow={caseRow} />
            ))}
          </div>
        </>
      )}
    </article>
  );
}

export default function ReaffirmationWorkboardPage() {
  const [data, setData] = useState(null);
  const [view, setView] = useState('attention');
  const [stage, setStage] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await apiFetch('/firm/reaffirmation-workboard/'));
    } catch (requestError) {
      setError(requestError.message || 'Could not load the reaffirmation workboard.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const cases = useMemo(() => {
    const searchValue = search.trim().toLowerCase();
    return (data?.cases || []).map((caseRow) => {
      const reaffirmations = caseRow.reaffirmations.filter((item) => {
        const matchesView = view === 'all'
          || (view === 'attention' && item.needs_attention)
          || (view === 'due_7' && item.needs_attention && Number.isFinite(caseRow.days_remaining) && caseRow.days_remaining >= 0 && caseRow.days_remaining <= 7)
          || (view === 'overdue' && item.needs_attention && caseRow.days_remaining < 0)
          || (view === 'final_not_filed' && item.attachments.final_signed && !item.filed)
          || (view === 'filed' && item.filed)
          || (view === 'complete' && item.stage === 'complete');
        const matchesStage = stage === 'all' || item.stage === stage;
        const haystack = [
          caseRow.debtor_names,
          caseRow.case_number,
          caseRow.case_id,
          item.creditor_name,
          ...(item.collateral || []),
        ].join(' ').toLowerCase();
        return matchesView && matchesStage && (!searchValue || haystack.includes(searchValue));
      });
      return { ...caseRow, reaffirmations };
    }).filter((caseRow) => caseRow.reaffirmations.length > 0);
  }, [data, search, stage, view]);

  const visibleCount = cases.reduce((total, caseRow) => total + caseRow.reaffirmations.length, 0);
  const summary = data?.summary || {};

  return (
    <DashboardShell>
      <div className="mx-auto max-w-[1500px]">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900">Reaffirmation Workboard</h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-600">
              Active filed Chapter 7 cases with a reaffirm election, their local signing progress, and whether a matching reaffirmation agreement appears on the court docket.
            </p>
          </div>
          <button type="button" onClick={load} disabled={loading} className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-60">
            <ArrowPathIcon className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {error && (
          <div className="mt-5 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">{error}</div>
        )}

        <div className="mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
          <SummaryCard label="Needs attention" value={summary.needs_attention} description="Not fully saved and filed" tone="red" active={view === 'attention'} onClick={() => setView('attention')} />
          <SummaryCard label="Due within 7 days" value={summary.due_within_7_days} description="Open items near deadline" tone="amber" active={view === 'due_7'} onClick={() => setView('due_7')} />
          <SummaryCard label="Final, not filed" value={summary.final_not_filed} description="Creditor filing follow-up" tone="blue" active={view === 'final_not_filed'} onClick={() => setView('final_not_filed')} />
          <SummaryCard label="Filed" value={summary.filed} description="Matched court dockets" tone="green" active={view === 'filed'} onClick={() => setView('filed')} />
          <SummaryCard label="All needed" value={summary.needed} description={`${summary.cases || 0} active Chapter 7 cases`} tone="slate" active={view === 'all'} onClick={() => setView('all')} />
        </div>

        <div className="mt-5 grid gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-[minmax(260px,1fr)_minmax(230px,320px)_auto]">
          <label className="relative">
            <span className="sr-only">Search workboard</span>
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-2.5 h-5 w-5 text-slate-400" />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search debtor, case, creditor, or collateral" className="w-full rounded-md border border-slate-300 py-2 pl-10 pr-3 text-sm" />
          </label>
          <select value={stage} onChange={(event) => setStage(event.target.value)} className="rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-700">
            <option value="all">All workflow stages</option>
            {Object.entries(STAGES).map(([value, detail]) => <option key={value} value={value}>{detail.label}</option>)}
          </select>
          <div className="flex items-center justify-end gap-2 text-sm text-slate-600">
            <ClockIcon className="h-5 w-5" />
            {visibleCount} item{visibleCount === 1 ? '' : 's'} shown
          </div>
        </div>

        {loading && !data ? (
          <div className="mt-8 rounded-xl border border-slate-200 bg-white p-12 text-center text-slate-500">
            <ArrowPathIcon className="mx-auto h-8 w-8 animate-spin" />
            <p className="mt-3">Checking reaffirmations and court dockets…</p>
          </div>
        ) : cases.length ? (
          <div className="mt-5 space-y-4">
            {cases.map((caseRow) => (
              <CaseCard key={caseRow.case_id} caseRow={caseRow} initiallyOpen={cases.length <= 12} />
            ))}
          </div>
        ) : (
          <div className="mt-8 rounded-xl border border-slate-200 bg-white p-12 text-center">
            <DocumentCheckIcon className="mx-auto h-10 w-10 text-emerald-600" />
            <h2 className="mt-3 text-lg font-semibold text-slate-900">No reaffirmations match this view</h2>
            <p className="mt-1 text-sm text-slate-500">Try another filter, or there may be no outstanding reaffirmation work.</p>
          </div>
        )}

        {summary.overdue > 0 && view !== 'overdue' && (
          <button type="button" onClick={() => setView('overdue')} className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-red-700 hover:text-red-600">
            <ExclamationTriangleIcon className="h-5 w-5" />
            Show {summary.overdue} overdue item{summary.overdue === 1 ? '' : 's'}
          </button>
        )}
      </div>
    </DashboardShell>
  );
}
